import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { vendorAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  Ship, Loader2, Upload, Check, FileText, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

function DocumentUploadItem({ doc, token, onUpload }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      await vendorAPI.uploadDocument(token, doc.type, file);
      toast.success(`${doc.label} uploaded successfully`);
      onUpload();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={`flex items-center gap-4 p-4 border rounded-lg transition-colors ${
      doc.uploaded ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200 hover:bg-slate-50'
    }`} data-testid={`vendor-doc-${doc.type}`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
        doc.uploaded ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
      }`}>
        {doc.uploaded ? <Check className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
      </div>
      <div className="flex-1">
        <p className="font-medium text-slate-900">{doc.label}</p>
        <p className="text-sm text-slate-500">
          {doc.uploaded ? 'Uploaded' : doc.required ? 'Required' : 'Optional'}
        </p>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={handleFileSelect}
        className="hidden"
      />
      {!doc.uploaded && (
        <Button 
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="bg-[#1F3A5F] hover:bg-[#102A43]"
          data-testid={`vendor-upload-btn-${doc.type}`}
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </>
          )}
        </Button>
      )}
    </div>
  );
}

export default function VendorPortalPage() {
  const { token } = useParams();
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadShipment();
  }, [token]);

  const loadShipment = async () => {
    try {
      const res = await vendorAPI.getShipment(token);
      setShipment(res.data);
    } catch (err) {
      setError(err.response?.status === 404 
        ? 'This link is invalid or has expired.' 
        : 'Failed to load shipment information.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="vendor-portal flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1F3A5F]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="vendor-portal flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-red-200 bg-red-50" data-testid="vendor-error-card">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-red-700 mb-2">Link Not Valid</h2>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const uploadedCount = shipment.documents.filter(d => d.uploaded).length;
  const totalCount = shipment.documents.length;
  const completion = Math.round((uploadedCount / totalCount) * 100);

  return (
    <div className="vendor-portal min-h-screen py-12 px-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#1F3A5F] rounded-xl flex items-center justify-center mx-auto mb-4">
            <Ship className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading font-bold text-2xl text-slate-900 mb-2">ImportDocs</h1>
          <p className="text-slate-600">Vendor Document Upload Portal</p>
        </div>
        
        {/* Shipment Info */}
        <Card className="border-slate-200 mb-6" data-testid="vendor-shipment-card">
          <CardHeader>
            <CardTitle className="font-heading">{shipment.name}</CardTitle>
            <CardDescription className="font-mono">{shipment.reference}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600">Document Progress</span>
              <span className="text-sm font-medium text-slate-900">{uploadedCount} of {totalCount} uploaded</span>
            </div>
            <Progress value={completion} className="h-3" />
          </CardContent>
        </Card>
        
        {/* Documents */}
        <Card className="border-slate-200" data-testid="vendor-documents-card">
          <CardHeader>
            <CardTitle className="font-heading">Required Documents</CardTitle>
            <CardDescription>
              Upload the following documents for this shipment. Accepted formats: PDF, JPG, PNG
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {shipment.documents.map((doc) => (
              <DocumentUploadItem 
                key={doc.type}
                doc={doc}
                token={token}
                onUpload={loadShipment}
              />
            ))}
          </CardContent>
        </Card>
        
        {/* Footer */}
        <p className="text-center text-sm text-slate-500 mt-8">
          This is a secure upload portal. You can only upload documents for this specific shipment.
        </p>
      </div>
    </div>
  );
}
